import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export const PaymentReturn = () => {
  const isSuccess = window.location.pathname === "/payment/success";
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  const [state, setState] = useState(isSuccess ? "checking" : "canceled");

  useEffect(() => {
    if (!isSuccess) return;
    if (!sessionId) {
      setState("failed");
      return;
    }
    let tries = 0;
    let active = true;
    const poll = async () => {
      tries += 1;
      try {
        const r = await api.paymentStatus(sessionId);
        if (!active) return;
        if (r.payment_status === "paid") {
          // Notify the dashboard tab (same origin) to refetch billing instantly.
          try {
            localStorage.setItem("firstrun_billing_refresh", String(Date.now()));
          } catch (_) {}
          return setState("paid");
        }
        if (["expired", "failed"].includes(r.payment_status)) return setState("failed");
      } catch (_) {}
      if (active && tries < 12) setTimeout(poll, 1500);
      else if (active) setState("failed");
    };
    poll();
    return () => {
      active = false;
    };
  }, [isSuccess, sessionId]);

  const go = () => (window.location.href = "/dashboard");

  const content = {
    checking: {
      icon: <Loader2 className="w-8 h-8 text-foreground animate-spin" />,
      title: "Confirming your payment…",
      desc: "Hang tight, this only takes a moment.",
      color: "border-border",
    },
    paid: {
      icon: <CheckCircle2 className="w-8 h-8 text-success" />,
      title: "You're on Pro! 🎉",
      desc: "Your subscription is active. You now have unlimited chats across your dashboard and embedded widget.",
      color: "border-success/30",
    },
    canceled: {
      icon: <XCircle className="w-8 h-8 text-muted-foreground" />,
      title: "Checkout canceled",
      desc: "No charge was made. You can upgrade anytime from your dashboard.",
      color: "border-border",
    },
    failed: {
      icon: <XCircle className="w-8 h-8 text-destructive" />,
      title: "We couldn't confirm your payment",
      desc: "If you were charged, your plan will update shortly. Otherwise, please try again.",
      color: "border-destructive/40",
    },
  }[state];

  return (
    <div className="min-h-screen text-foreground flex items-center justify-center px-4">
      <div
        data-testid="payment-return"
        data-payment-state={state}
        className={`w-full max-w-md text-center border ${content.color} paper rounded-2xl p-8 fade-up`}
      >
        <div className="w-16 h-16 mx-auto rounded-2xl bg-secondary flex items-center justify-center mb-5">
          {content.icon}
        </div>
        <h1 className="font-heading text-4xl text-foreground">{content.title}</h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{content.desc}</p>
        {state !== "checking" && (
          <button
            data-testid="return-home-button"
            onClick={go}
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-xl paper-dark text-primary-foreground font-medium"
          >
            Go to dashboard <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
